import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLike, useHasLiked } from '@/hooks/useGallery';
import { Heart } from '../ui/Heart';

interface LikeButtonProps {
  photoId: string;
  initialCount: number;
  compact?: boolean;
}

/** Pre-computed particle directions so each click sends a small, varied burst. */
const PARTICLES = [
  { x: -22, y: -28, r: -25 },
  { x:  18, y: -32, r:  20 },
  { x: -30, y:  -6, r:  35 },
  { x:  28, y:  -8, r: -35 },
  { x:  -8, y: -36, r:   5 },
  { x:  10, y: -30, r: -10 },
];

export default function LikeButton({ photoId, initialCount, compact }: LikeButtonProps) {
  const { data: serverLiked } = useHasLiked(photoId);
  const [liked, setLiked] = useState(false);
  const [count, setCount] = useState(initialCount);
  // burst key increments each click so AnimatePresence can re-trigger
  const [burstKey, setBurstKey] = useState(0);
  const { mutate: toggleLike } = useLike(photoId);

  useEffect(() => {
    if (serverLiked !== undefined) {
      setLiked(serverLiked);
    }
  }, [serverLiked]);

  useEffect(() => {
    setCount(initialCount);
  }, [initialCount]);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const nextLiked = !liked;
    setLiked(nextLiked);
    setCount((prev) => (nextLiked ? prev + 1 : Math.max(0, prev - 1)));
    if (nextLiked) setBurstKey((k) => k + 1);
    toggleLike();
  };

  return (
    <button
      onClick={handleClick}
      className="relative flex items-center gap-xs text-sm text-fg-soft transition-colors hover:text-fg"
      aria-label={liked ? 'Unlike' : 'Like'}
      aria-pressed={liked}
    >
      <span className="relative flex h-5 w-5 items-center justify-center">
        {/* glow ring — appears on like, fades quickly */}
        <AnimatePresence>
          {liked && (
            <motion.span
              key="ring"
              initial={{ scale: 0.6, opacity: 0.55 }}
              animate={{ scale: 2.4, opacity: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className="absolute inset-0 rounded-full"
              style={{
                background:
                  'radial-gradient(circle, color-mix(in srgb, var(--color-error) 50%, transparent) 0%, transparent 70%)',
              }}
            />
          )}
        </AnimatePresence>

        <motion.span
          key="heart"
          initial={false}
          animate={
            liked
              ? { scale: [1, 1.55, 0.92, 1.15, 1] }
              : { scale: 1 }
          }
          transition={{ duration: 0.55, times: [0, 0.18, 0.4, 0.7, 1], ease: 'easeOut' }}
          className="leading-none"
          style={{
            color: liked ? 'var(--color-error)' : 'var(--color-fg-soft)',
            display: 'inline-flex',
          }}
        >
          <Heart filled={liked} />
        </motion.span>

        {/* particle burst on like */}
        <AnimatePresence>
          {!liked ? null : (
            <motion.span
              key={`burst-${burstKey}`}
              className="pointer-events-none absolute inset-0"
            >
              {PARTICLES.map((p, i) => (
                <motion.span
                  key={i}
                  initial={{ x: 0, y: 0, opacity: 1, scale: 0.6, rotate: 0 }}
                  animate={{ x: p.x, y: p.y, opacity: 0, scale: 0.2, rotate: p.r }}
                  transition={{ duration: 0.7, ease: 'easeOut', delay: 0.05 }}
                  className="absolute left-1/2 top-1/2 block h-1.5 w-1.5 rounded-full"
                  style={{
                    background: 'var(--color-error)',
                    transformOrigin: 'center',
                  }}
                />
              ))}
            </motion.span>
          )}
        </AnimatePresence>
      </span>

      <motion.span
        key={count}
        initial={{ y: -6, opacity: 0.4 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className={liked ? 'font-semibold text-error text-xs' : 'text-xs text-fg-soft'}
      >
        {!compact && count}
        {compact && count > 0 && count}
      </motion.span>
    </button>
  );
}
